import React, { useRef, useState } from "react";
import { css, styled } from "styled-components";

export const PageWrapper = styled.div<{ $padding?: string }>`
  padding: ${(p) => p.$padding ?? "0 20px 100px"};
  display: flex;
  flex-direction: column;
`;

const TwoColumnOuter = styled.div<{ $padding?: string; $minWidth: string }>`
  padding: ${(p) => p.$padding ?? "0 20px 100px"};
  display: flex;
  flex-direction: column;
  row-gap: 20px;

  /* \`align-items: stretch\` (the flex default) rather than \`flex-start\`: a \`position: sticky\`
   * pane can only stay stuck within its OWN containing block's height, which for a flex item is
   * its own natural content height under \`flex-start\` — shorter than the other pane whenever one
   * column (e.g. a long field list) is taller than the other (e.g. a short sidebar). That left a
   * sticky sidebar un-sticking early and scrolling away well before the taller column finished
   * scrolling. Stretching both panes to the row's full height gives the sticky pane's containing
   * block enough vertical room to stay stuck for the whole scroll, only releasing at the very
   * bottom — same as a fixed header, just still document-flow. Only applies at this row-layout
   * breakpoint, so the stacked mobile/narrow layout (\`flex-direction: column\` above) is
   * unaffected. */
  @media (min-width: ${(p) => p.$minWidth}) {
    flex-direction: row;
    align-items: stretch;
  }
`;

const TwoColumnPane = styled.div<{ $minWidth: string }>`
  min-width: 0;
  flex: 1 1 auto;

  @media (min-width: ${(p) => p.$minWidth}) {
    flex: var(--pane-flex, 1) 1 0;
  }
`;

const ResizeHandle = styled.div<{ $minWidth: string; $stickyTop?: string }>`
  flex: 0 0 12px;
  cursor: ew-resize;
  position: relative;
  align-self: stretch;
  /* A sticky right pane (e.g. an \`Aside\` with a negative side margin for its own scroll-shadow
   * room) always creates its own stacking context and, being later in the DOM, paints above this
   * handle wherever their boxes overlap — leaving no way to grab the handle at that edge. An
   * explicit z-index puts the handle in a higher stacking layer so it wins regardless of DOM
   * order. */
  z-index: 2;

  @media (max-width: ${(p) => p.$minWidth}) {
    display: none;
  }

  /* When the right pane owns its own sticky sidebar (e.g. a fixed-height, self-scrolling Aside),
   * a plain "stretch to the row's full height" handle spans the ENTIRE (often page-length) row —
   * at any given scroll position only some arbitrary sliver of that giant bar is on screen, so it
   * visually disconnects from the sidebar it's supposed to sit next to. Pinning the handle itself
   * with the same top/height keeps it visually tracking the sticky pane instead. */
  ${(p) =>
    p.$stickyTop &&
    css`
      @media (min-width: ${p.$minWidth}) {
        position: sticky;
        top: ${p.$stickyTop};
        align-self: flex-start;
        height: calc(100vh - ${p.$stickyTop});
      }
    `}
`;

function useColumnRatio(
  id: string | undefined,
  defaultRatio: number
): [number, (r: number) => void] {
  const storageKey = id ? `cs_page_ratio_${id}` : null;

  const [ratio, setRatioState] = useState<number>(() => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
        const parsed = parseFloat(parseFloat(stored).toFixed(1));
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
    return defaultRatio;
  });

  const setRatio = (r: number) => {
    setRatioState(r);
    if (storageKey) {
      localStorage.setItem(storageKey, String(r));
    }
  };

  return [ratio, setRatio];
}

interface PageWrapperTwoColumnProps {
  id?: string;
  minWidth?: string;
  padding?: string;
  defaultRatio?: number;
  /** Set this when the right pane owns its own `position: sticky` sidebar (a fixed-height,
   *  self-scrolling Aside) — pins the resize handle to the same `top`/height so it tracks the
   *  sticky pane across a full-page scroll instead of spanning the whole (possibly page-length)
   *  row. Pass the same CSS `top` value the sticky pane itself uses. */
  stickyTop?: string;
  children: React.ReactNode;
  className?: string;
}

export function PageWrapperTwoColumn({
  id,
  minWidth: $minWidth = "1030px",
  padding,
  defaultRatio = 0.5,
  stickyTop,
  children,
  className,
}: PageWrapperTwoColumnProps) {
  const [ratio, setRatio] = useColumnRatio(id, defaultRatio);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef<number>(0);
  const dragStartRatio = useRef<number>(0);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartRatio.current = ratio;

    const onMouseMove = (e: MouseEvent) => {
      const containerWidth =
        containerRef.current?.getBoundingClientRect().width ?? 1;
      const usableWidth = containerWidth - 15; // subtract handle width
      const minRatio = 400 / usableWidth;
      const maxRatio = 1 - minRatio;
      const delta = e.clientX - dragStartX.current;
      const deltaRatio = delta / containerWidth;
      setRatio(
        Math.min(
          maxRatio,
          Math.max(minRatio, dragStartRatio.current + deltaRatio)
        )
      );
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const [left, right] = React.Children.toArray(children);

  return (
    <TwoColumnOuter
      ref={containerRef}
      $minWidth={$minWidth}
      $padding={padding}
      className={className}
    >
      <TwoColumnPane
        $minWidth={$minWidth}
        style={{ "--pane-flex": ratio } as React.CSSProperties}
      >
        {left}
      </TwoColumnPane>
      <ResizeHandle
        $minWidth={$minWidth}
        $stickyTop={stickyTop}
        onMouseDown={onMouseDown}
      />
      <TwoColumnPane
        $minWidth={$minWidth}
        style={{ "--pane-flex": 1 - ratio } as React.CSSProperties}
      >
        {right}
      </TwoColumnPane>
    </TwoColumnOuter>
  );
}
